import { JFD, JFDValues, JFL, JFLGroup, Modality } from '@le2/jfd'

import { JFLGroupRefProvider } from '../../components/JFDRender/utils'
import { BookingEnrollmentStatus, BookingImages } from '../../lib/api/booking'
import { Booking as BookingAPI } from '../../lib/api/booking'
import { DeviceInfo } from '../../nativeExtension/IWNativeExtDeviceDefines'
import { JFDStrategy } from './smtCapture/jfdStrategy'

export enum Status {
  Idle = 'Idle',
  Loading = 'Loading',
  Error = 'Error',
  Complete = 'Complete'
}

export interface ValidationResult {
  status: Status
  message: string
}

export function newValidationResult(status: Status, message: string = ''): ValidationResult {
  return { status, message }
}

export interface Booking {
  bookingNumber: string
  enrollmentStatus: BookingEnrollmentStatus
  createdAt: Date | undefined
  updatedAt: Date | undefined
  submittedAt: Date | undefined
  isActive: boolean
  isSealed: boolean
  jfd: JFD
  originalJFD: {
    values: JFDValues
    errors: JFDValues
  }
  bookingJFL: JFL
  jflGroupRefProvider: JFLGroupRefProvider
  jfdStrategy: JFDStrategy
  bookingImages: BookingImages
  hasSaveButtonBeenClicked: boolean
  saveStatus: Status
  submitStatus: Status
  isLoadFromFile: boolean
  isOnlyMandatory: boolean

  captureDevices: Map<Modality, DeviceInfo>

  /**
   * Indicates if we reached the max number of images for the Modality (they key)
   */
  maxImages: {
    [Modality.Face]: boolean
    [Modality.SMT]: boolean
  }

  validation: {
    biographics: ValidationResult
    [Modality.Face]: ValidationResult
    [Modality.SMT]: ValidationResult
    [Modality.Fingerprint]: ValidationResult
    [Modality.Palm]: ValidationResult
    [Modality.Iris]: ValidationResult
  }

  setValidationStatus: (modality: keyof Booking['validation'], result: ValidationResult) => void

  /**
   * If we should allow the user to submit a booking even if it has some errors.
   */
  bypassSubmitValidations: boolean

  /**
   * The Mapped Booking from the API to object.
   */
  mappedBooking: BookingAPI

  /**
   * Used to determine if we should ask confirmation before leaving the page.
   * Returns `true` when we have unsaved data.
   */
  isDirty: () => boolean
}

export interface BiographicsSectionValidationResult {
  status: Status
  errorCount: number
}

export function newBiographicsSectionValidationResult(
  status: Status,
  errorCount: number = 0
): BiographicsSectionValidationResult {
  return { status, errorCount }
}

export interface BiographicSection extends JFLGroup, BiographicsSectionValidationResult {
  label: string
  jflGroup: JFLGroup
}

export interface BiographicsState {
  sections: BiographicSection[]
}

export interface BiographicsStateComputedValues {
  errorCount: number
  errorCountLabel: string
}

export interface BookingComputedValues {
  isLoading: boolean
  isSubmitEnabled: boolean
}

export interface GenerateBookingNumResponse {
  bookingNum: string
}
